export enum Persona {
  MERCHANT = "merchant",
  DISTRIBUTOR = "distributor",
  INVESTOR = "investor",
  MERCHANT_SHAREHOLDER = "merchant-shareholder",
  MERCHANT_DIRECTOR = "merchant-director",
  INVESTOR_SHAREHOLDER = "investor-shareholder",
  INVESTOR_DIRECTOR = "investor-director",
  MERCHANT_SPV = "merchant-spv",
  OTHERS = "others",
  AUTH_REP = "auth-rep",
  ISPV = "ispv",
  FUNDINGSPV = "funding-spv",
  ISSUER_DIRECTOR = "issuer-director",
}

/**
 * Canonical KYC status values matching the backend wire format. Production
 * consumers compare against these lowercase snake-case strings directly
 * (e.g. `kycStatus === "approved"`). Test fixtures and factories should use
 * these enum members rather than hand-typed strings to avoid casing drift.
 */
export enum KYCStatus {
  APPROVED = "approved",
  PENDING_APPROVAL = "pending_approval",
  PENDING_RESUBMISSION = "pending_resubmission",
  REJECTED = "rejected",
}

export enum SpvStatus {
  PENDING_INITIATION = "pending-initiation",
  INITIATED = "initiated",
  COMPLETED = "completed",
}
export enum InvestorCategory {
  PRIVATE_INDIVIDUAL = "Private Placement Individual",
  PRIVATE_INSTITUTION = "Private Placement Investor",
  JUNIOR_INDIVIDUAL = "Junior Individual Investor",
  JUNIOR_INSTITUTION = "Junior Corporate Investor",
}

export enum SVPCategory {
  ISPV = "ispv",
  FUNDINGSPV = "funding-spv",
}

export enum EntityTypeEnum {
  INDIVIDUAL = "individual",
  INSTITUTION = "institution",
}

export enum InvestorCategoryEnum {
  JUNIOR = "junior",
  SENIOR = "senior",
}

export enum InvestmentMode {
  AUTO = "auto",
  MANUAL = "manual",
}

export enum CompanyRole {
  SHAREHOLDER = "Shareholder",
  DIRECTOR = "Director",
  BOTH = "Both",
}

export enum ShareholderDirectorField {
  ROLE = "role",
  EMAIL = "email",
  FIRSTNAME = "firstName",
  LASTNAME = "lastName",
}

export enum IspvStatusEnum {
  AVAILABLE = "AVAILABLE",
  UNAVAILABLE = "UNAVAILABLE",
}

export enum FileType {
  PDF = "pdf",
  CSV = "csv",
  DOCX = "docx",
}

export enum SanctionIOStatus {
  APPROVED = "approved",
  PENDING = "pending",
  REJECTED = "rejected",
}

export enum DocuSignEventHandler {
  READY = "ready",
  SESSION_END = "sessionEnd",
}

export enum DocuSignEvent {
  SIGNING_COMPLETE = "signing_complete",
  CANCEL = "cancel",
  DECLINE = "decline",
  EXCEPTION = "exception",
  FAX_PENDING = "fax_pending",
  SESSION_TIMEOUT = "session_timeout",
  TTL_EXPIRED = "ttl_expired",
  VIEWING_COMPLETE = "viewing_complete",
}

export enum File {
  BUSINESS_PROFILE = "bussiness_profile",
  SHAREHOLDERS_DIRECTORS_REGISTERS = "shareholders_directors_registers",
  BALANCE_SHEET = "balance_sheet",
  PROOF_OF_ADDRESS = "proof_of_address",
  BANK_STATEMENT = "bank_statement",
  FINANCIAL_STATEMENT = "financial_statement",
  SALARY_PROOF = "salary_proof",
  INVESTMENT_MANDATE = "investment_mandate",
  FUND_MANAGER_LICENSE = "fund_manager_license",
  CERTIFICATE_OF_INCORPORATION = "certificate_of_incorporation",
  SUPPORTING_DOCUMENTS_WITH_SUPPLIERS = "supporting_documents_with_suppliers",
  PROOF_OF_BANK_ACCOUNT_DETAILS = "proof_of_bank_account_details",
  CRS_SELF_CERTIFICATION_FORM = "crs_self_certification_form",
  COMPANY_CONSTITUTION = "company_constitution",
}

export enum SpvFile {
  SPV_BUSINESS_PROFILE = "spv_business_profile",
  SPV_COMPANY_CONSTITUTION = "spv_company_constitution",
  SPV_CONSENT = "spv_consent",
  SPV_CERTIFICATE_OF_INCORPORATION = "spv_certificate_of_incorporation",
  SPV_REGISTER_OF_MEMBERS = "spv_register_of_members",
  SPV_REGISTER_OF_DIRECTORS = "spv_register_of_directors",
  SPV_ORGANIZATION_CHART = "spv_organization_chart",
  SPV_PROOF_OF_ADDRESS = "spv_proof_of_address",
  SPV_BANK_STATEMENT = "spv_bank_statement",
  SPV_BALANCE_SHEET = "spv_balance_sheet",
}

export enum SkuLoanApplicationPaymentTerm {
  CASH_ON_DELIVERY = "cash-on-delivery",
  PARTIAL_PAYMENT = "partial-payment",
  PAYMENT_ON_PURCHASE_ORDER = "payment-on-purchase-order",
}

export enum OcrType {
  PO = "purchaseOrder",
}

export enum RegtankOnboardingStatusEnum {
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
  LIVENESS_FAILED = "LIVENESS_FAILED",
  LIVENESS_STARTED = "LIVENESS_STARTED",
  ID_UPLOADED = "ID_UPLOADED",
  PROCESSING = "PROCESSING",
  LIVENESS_PASSED = "LIVENESS_PASSED",
  URL_GENERATED = "URL_GENERATED",
  ID_UPLOADED_FAILED = "ID_UPLOADED_FAILED",
  CAMERA_FAILED = "CAMERA_FAILED",
}

export enum RegtankOnboardingFHStatusEnum {
  PASSED = "PASSED",
  PENDING_REVIEW = "PENDING_REVIEW",
  PENDING_VERIFICATION = "PENDING_VERIFICATION",
  REATTEMPT_REJECTED = "REATTEMPT_REJECTED",
  PENDING_REATTEMPT = "PENDING_REATTEMPT",
}

export enum LivenessWebVerifyStatusEnum {
  LIVENESS_FAILED = "LIVENESS_FAILED",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export enum RegtankReattemptApprovalEnum {
  ALL = "ALL",
  NO_ACTION_REQUIRED = "NO ACTION REQUIRED",
  ACTION_REQUIRED = "ACTION REQUIRED",
}

export enum OnboardingStatusEnum {
  ONBOARDING_IN_DRAFT = "onboarding_in_draft",
  PENDING_SUBMISSION = "pending_submission",
  ONBOARDING_RESUBMISSION_IN_DRAFT = "onboarding_resubmission_in_draft",
  PENDING_RESUBMISSION = "pending_resubmission",
  PENDING_APPROVAL = "pending_approval",
  APPROVED = "approved",
  REJECTED = "rejected",
}

export enum RegtankLivenessFHStatusEnum {
  PASSED = "PASSED",
  PENDING_REVIEW = "PENDING_REVIEW",
  NOTHING = "NOTHING",
  REATTEMPT_REJECTED = "REATTEMPT_REJECTED",
  PENDING_REATTEMPT = "PENDING_REATTEMPT",
}

export enum RoleEnum {
  merchant = "merchant",
  investor = "investor",
  issuer = "ispv",
}

export enum DocumentTitleEnum {
  INVESTOR_AGREEMENT = "Investor Agreement",
  BOARD_RESOLUTION_CORPORATE_INVESTOR = "Board Resolution_Corporate Investor",
  BOARD_RESOLUTION_MERCHANT = "Board Resolution_Merchant",
  REVENUE_SHARING_AGREEMENT = "Revenue Sharing Agreement",
  NOTE_SUBSCRIPTION_AGREEMENT = "Note Subscription Agreement",
  MEMBERSHIP_AGREEMENT = "Membership Agreement",
  BOARD_RESOLUTION_ISSUER = "Board Resolution_Issuer",
  ISSUER_AGREEMENT = "Issuer Agreement",
  INVESTOR_AGREEMENT_CORPORATE = "Investor Agreement_Corporate Investor",
}

export enum ThanksPageType {
  EKYC = "ekyc",
  ONBOARDING = "onboarding",
  RESUBMITTED = "resubmitted",
  RESUBMITTED_IND_INVESTOR = "resubmitted_ind_investor",
  AGREEMENT = "agreement",
  DEFAULT = "default",
  COMPLETED = "completed",
  PENDING_AGREEMENT = "pending_agreement",
  BOARDING_RESOLUTION = "boarding_resolution",
  ONBOARDING_SUPPLIER = "onboarding_supplier",
}

export enum VAAccountTypes {
  INVESTOR = "investor",
  INVESTOR_EXTERNAL = "investor_external",
  SF = "sf",
  ASF = "asf",
  MOE = "qc",
  QC = "qc", // TODO: moe will be deprecated to qc
  SKU = "sku",
  SN = "sn",
  FH_ADMIN = "fh_admin",
  DISTRIBUTER = "distributer",
  MERCHANT = "merchant",
  MERCHANT_BENEFICIARY = "merchant_beneficiary",
  INVESTOR_BENEFICIARY = "investor_beneficiary",
  UNCLAIMED_MONEY = "unclaimed_money",
  FUNDEDHERE = "fundedhere",
  MERCHANT_EXTERNAL = "merchant_external",
  THIRD_PARTY_LOGISTICS = "3pl",
  PSP = "psp",
  OTHERS = "others",
}

/**
 * Admin maker-checker approval workflow. A maker creates an approval request of
 * one ApprovalActionType; a checker moves it through ApprovalRequestStatus.
 * Relocated here from the admin service files so the common Zod schemas
 * (schemas/approval.ts) can reference them via z.nativeEnum without inverting
 * the common → admin dependency direction.
 */
export enum ApprovalActionType {
  DISBURSEMENT = "disbursement",
  BYPASS_MERCHANT_NOTE_AGREEMENT = "bypass_merchant_note_agreement",
  BYPASS_MERCHANT_COD = "bypass_merchant_cod",
  EXTERNAL_PAYMENT = "external_payment",
  FUND_PULL = "fund_pull",
}

export enum ApprovalRequestStatus {
  PENDING_APPROVAL = "pending_approval",
  APPROVED = "approved",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
  PENDING_RESUBMISSION = "pending_resubmission",
  ERROR = "error",
}

// Receiver category for external-payment approval requests.
export enum ExternalReceiverType {
  THIRD_PARTY_LOGISTICS = "third_party_logistics",
  OTHERS = "others",
}
